import { FormattedNumber } from 'react-intl'
import { withHandlers } from 'recompose'
import classNames from 'classnames'
import moment from 'moment'

import TableCell from '@components/TableCell'
import { TableCell as TableCellCopy } from '@components/Table'
import Button from '@components/Button'
import Icon from '@components/Icon'

import { currencyFromSymbol } from 'shared/currency-utilities'
import { isMobileAgent } from 'shared/responsive-helpers'
import featureToggles from 'shared/featureToggles'
import { formatToMultilineDateTime } from 'shared/time-utilites'

import { PAYMENT_MODE_BOLETO, PAYMENT_MODE_CREDIT_CARD } from 'shared/transactions'

import { RESERVATION_IN_PROGRESS } from '@booking/constants'
import { reservationSelector, statusSelector, cancellationSelector } from '../../selectors'
import { reservationCancelAmendPolicyPendingSelector, cancelReservationPendingSelector } from 'grs/selectors'
import { STATUS_ACTIVE } from 'grs/constants'
import {
    downloadReceipt,
    getReservationCancelAmendPolicy,
    closeCancellationModal,
    cancelReservation,
    confirmCancelReservation,
} from '../../actions'
import { startReservationExtension } from 'extending/actions'

import styles from '../styles.module.scss'

const enhance = compose(
    connect(
        createStructuredSelector({
            cancelAmendPolicyReservationPending: reservationCancelAmendPolicyPendingSelector,
            cancelReservationPending: cancelReservationPendingSelector,
            reservation: reservationSelector,
            status: statusSelector,
            cancellation: cancellationSelector,
        }),
        {
            downloadReceipt,
            getReservationCancelAmendPolicy,
            startReservationExtension,
            cancelReservation,
            closeCancellationModal,
            confirmCancelReservation,
        }
    ),
    withHandlers({
        onClick:
            ({ reservationId, onClick }) =>
            () =>
                onClick(reservationId),
        onClickDownloadReceipt:
            ({ reservationId, downloadReceipt }) =>
            e => {
                e.stopPropagation()
                downloadReceipt(reservationId, isMobileAgent() ? open() : undefined)
            },
    })
)
const Reservation = ({
    reservation: {
        confirmationNumber,
        createdDate,
        isMonthlyPass,
        location,
        fromDate,
        toDate,
        currencySymbol,
        amount,
        reservationId,
        allowExtend,
        status: reservationStatus,
        paymentMode,
        digitableLine,
    },
    onClick,
    onClickDownloadReceipt,
    getReservationCancelAmendPolicy,
    startReservationExtension,
}) => {
    const getCurrentPaymentMethod = () => {
        if (paymentMode === PAYMENT_MODE_BOLETO)
            return (
                <span className={digitableLine && styles.paymentMethodAddIcon}>
                    <T>dashboard.transaction.payment.boleto</T>
                </span>
            )
        if (paymentMode === PAYMENT_MODE_CREDIT_CARD)
            return (
                <span>
                    <T>dashboard.transaction.payment.card</T>
                </span>
            )
        if (paymentMode === null) return ''
    }

    const showCopyButton = digitableLine !== null
    const showPaymentMethodTableCell = !featureToggles.caPurchaseFlow

    const textAreaRef = React.useRef(null)
    const [isCodeCopied, setCodeCopied] = React.useState(false)
    const [isTooltipVisible, setTooltipVisible] = React.useState(false)

    const copyToClipboard = event => {
        event.stopPropagation()
        textAreaRef.current.value = digitableLine
        textAreaRef.current.select()

        if (document.execCommand('copy')) {
            document.execCommand('copy')
            setCodeCopied(true)
        }
    }

    React.useEffect(() => {
        if (isCodeCopied) {
            setTooltipVisible(true)
        }

        const timeout = setTimeout(() => {
            setTooltipVisible(false)
        }, 1500)

        return () => {
            clearTimeout(timeout)
            setTooltipVisible(false)
        }
    }, [isCodeCopied])

    React.useEffect(() => {
        if (!isTooltipVisible) {
            setCodeCopied(false)
        }
    }, [isTooltipVisible])

    return (
        <>
            <div className={styles.tableRow} onClick={onClick}>
                <TableCell fieldName={<T>dashboard.label.transaction</T>} className={styles.transaction}>
                    <span>{confirmationNumber}</span>
                    <span>{formatToMultilineDateTime(moment(createdDate))}</span>
                </TableCell>
                <TableCell fieldName={<T>dashboard.label.type</T>} className={styles.type}>
                    <T>{isMonthlyPass ? 'dashboard.label.subscription' : 'dashboard.label.booking'}</T>
                </TableCell>
                <TableCell
                    fieldName={<T>dashboard.label.location</T>}
                    className={classNames(styles.location, showPaymentMethodTableCell && styles.locationSmallSize)}>
                    {location}
                </TableCell>
                <TableCell fieldName={<T>dashboard.label.range.s.date</T>} className={styles.start}>
                    {formatToMultilineDateTime(moment(fromDate))}
                </TableCell>
                <TableCell fieldName={<T>dashboard.label.range.e.date</T>} className={styles.end}>
                    {formatToMultilineDateTime(moment(toDate))}
                    {allowExtend && (
                        <Button
                            className={styles.buttonExtendEndDate}
                            btnStyle="link"
                            iconLeft="arrow-right"
                            href="/extending"
                            onClick={e => {
                                e.stopPropagation()
                                startReservationExtension(reservationId)
                                getReservationCancelAmendPolicy(reservationId)
                            }}>
                            <T>dashboard.label.extend</T>
                        </Button>
                    )}
                </TableCell>
                <TableCell fieldName={<T>dashboard.label.price</T>} className={styles.price}>
                    <FormattedNumber value={amount} style="currency" currency={currencyFromSymbol(currencySymbol)} />
                </TableCell>
                {showPaymentMethodTableCell && (
                    <TableCellCopy
                        onClick={event => copyToClipboard(event)}
                        className={styles.reservationPaymentMethod}
                        title={<T>dashboard.label.payment.method</T>}
                        text={getCurrentPaymentMethod()}
                        allowCopy
                        hasIconBtn={showCopyButton}
                        textAreaRef={textAreaRef}
                        isTooltipVisible={isTooltipVisible}
                        tooltipText={<T>dashboard.transaction.digitable.line.copied</T>}
                        onIconClick={event => copyToClipboard(event)}
                    />
                )}
                <TableCell
                    fieldName={reservationStatus !== RESERVATION_IN_PROGRESS && <T>dashboard.label.receipt</T>}
                    className={styles.actions}>
                    <div className="d-flex align-items-center justify-content-around">
                        {reservationStatus !== RESERVATION_IN_PROGRESS && (
                            <button className={styles.download} onClick={onClickDownloadReceipt} />
                        )}
                        {reservationStatus === STATUS_ACTIVE && <Icon type="edit" onClick={onClick} />}
                    </div>
                </TableCell>
            </div>
        </>
    )
}

export default enhance(Reservation)
